import React from 'react';
import './Tabs.css';

export interface WxbTabItem {
  key: string;
  label: React.ReactNode;
  children?: React.ReactNode;
  disabled?: boolean;
}

export interface WxbTabsProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'onChange'> {
  items: WxbTabItem[];
  activeKey?: string;
  defaultActiveKey?: string;
  onChange?: (key: string) => void;
}

const firstEnabledKey = (items: WxbTabItem[]) => items.find((item) => !item.disabled)?.key || '';

export const WxbTabs: React.FC<WxbTabsProps> = ({
  items,
  activeKey: controlledKey,
  defaultActiveKey,
  onChange,
  className = '',
  'aria-label': ariaLabel = '内容标签页',
  ...props
}) => {
  const defaultKey = items.some((item) => item.key === defaultActiveKey && !item.disabled)
    ? defaultActiveKey!
    : firstEnabledKey(items);
  const [internal, setInternal] = React.useState(defaultKey);
  const isControlled = controlledKey !== undefined;
  const requestedKey = isControlled ? controlledKey : internal;
  const current = items.some((item) => item.key === requestedKey && !item.disabled)
    ? requestedKey
    : firstEnabledKey(items);
  const tabsRef = React.useRef<HTMLDivElement>(null);
  const tabRefs = React.useRef(new Map<string, HTMLButtonElement>());
  const componentId = React.useId().replace(/:/g, '');

  const updateInk = React.useCallback(() => {
    const nav = tabsRef.current;
    const activeTab = tabRefs.current.get(current);
    if (!nav || !activeTab) return;
    nav.style.setProperty('--wxb-tabs-ink-left', `${activeTab.offsetLeft}px`);
    nav.style.setProperty('--wxb-tabs-ink-width', `${activeTab.offsetWidth}px`);
  }, [current]);

  React.useEffect(() => {
    updateInk();
    const nav = tabsRef.current;
    if (!nav || typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(updateInk);
    observer.observe(nav);
    return () => observer.disconnect();
  }, [items, updateInk]);

  React.useEffect(() => {
    if (isControlled || internal === current) return;
    setInternal(current);
  }, [current, internal, isControlled]);

  const selectTab = React.useCallback((key: string) => {
    const item = items.find((candidate) => candidate.key === key);
    if (!item || item.disabled) return;
    if (!isControlled) setInternal(key);
    onChange?.(key);
  }, [isControlled, items, onChange]);

  const focusAndSelect = (item: WxbTabItem) => {
    tabRefs.current.get(item.key)?.focus();
    selectTab(item.key);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>, item: WxbTabItem) => {
    const enabledItems = items.filter((candidate) => !candidate.disabled);
    const currentIndex = enabledItems.findIndex((candidate) => candidate.key === item.key);
    if (currentIndex < 0 || enabledItems.length === 0) return;

    let target: WxbTabItem | undefined;
    if (event.key === 'ArrowRight') target = enabledItems[(currentIndex + 1) % enabledItems.length];
    if (event.key === 'ArrowLeft') target = enabledItems[(currentIndex - 1 + enabledItems.length) % enabledItems.length];
    if (event.key === 'Home') target = enabledItems[0];
    if (event.key === 'End') target = enabledItems[enabledItems.length - 1];
    if (!target) return;

    event.preventDefault();
    focusAndSelect(target);
  };

  const activePanelIndex = items.findIndex((item) => item.key === current);
  const activePanel = activePanelIndex >= 0 ? items[activePanelIndex] : undefined;
  const tabId = (index: number) => `wxb-tabs-${componentId}-tab-${index}`;
  const panelId = (index: number) => `wxb-tabs-${componentId}-panel-${index}`;

  return (
    <div className={`wxb-tabs ${className}`} {...props}>
      <div className="wxb-tabs-nav" ref={tabsRef} role="tablist" aria-label={ariaLabel}>
        {items.map((item, index) => {
          const selected = current === item.key;
          const hasPanel = item.children !== undefined;
          return (
            <button
              key={item.key}
              ref={(node) => {
                if (node) tabRefs.current.set(item.key, node);
                else tabRefs.current.delete(item.key);
              }}
              id={tabId(index)}
              type="button"
              role="tab"
              aria-selected={selected}
              aria-controls={hasPanel ? panelId(index) : undefined}
              tabIndex={selected ? 0 : -1}
              disabled={item.disabled}
              className={`wxb-tab-btn ${selected ? 'is-active' : ''} ${item.disabled ? 'is-disabled' : ''}`}
              onClick={() => selectTab(item.key)}
              onKeyDown={(event) => handleKeyDown(event, item)}
            >
              {item.label}
            </button>
          );
        })}
        <span className="wxb-tabs-ink" aria-hidden="true" />
      </div>
      {activePanel && activePanel.children !== undefined && (
        <div
          id={panelId(activePanelIndex)}
          className="wxb-tabs-panel"
          role="tabpanel"
          aria-labelledby={tabId(activePanelIndex)}
          tabIndex={0}
        >
          {activePanel.children}
        </div>
      )}
    </div>
  );
};
